/**
 * Admin Layout Component
 * Sidebar navigation and protected admin area
 */

import { useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../hooks/useAuth';
import {
  Users,
  LayoutDashboard,
  Activity,
  BarChart3,
  Building2,
  LogOut,
  Palette,
  Mail,
  CreditCard,
  Flag,
  GraduationCap,
  ClipboardList,
  Inbox,
  LifeBuoy,
  Send,
  PackageCheck,
  ScrollText,
  Globe,
  DoorOpen,
  HeartPulse,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { isAuthenticated, isAdmin, signOut, user, loading } = useAuth();
  const navigate = useNavigate();

  // Protect admin routes
  useEffect(() => {
    // Don't redirect while still loading auth state
    if (loading) {
      console.log('🔄 AdminLayout: Still loading auth state...');
      return;
    }

    console.log('🔍 AdminLayout: Auth state:', {
      isAuthenticated,
      isAdmin,
      loading,
      userEmail: user?.email
    });

    if (!isAuthenticated) {
      console.log('❌ AdminLayout: Not authenticated, redirecting to login');
      navigate({ to: '/login', search: { redirect: '/admin' } });
    } else if (!isAdmin) {
      // Redirect non-admin users
      console.log('❌ AdminLayout: Not admin, redirecting to home');
      navigate({ to: '/', search: { slide: undefined } });
    } else {
      console.log('✅ AdminLayout: User is authenticated admin');
    }
  }, [isAuthenticated, isAdmin, loading, navigate, user]);

  // Show loading while checking auth
  if (loading || !isAuthenticated || !isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: '/login', search: undefined });
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Customer Health', href: '/admin/customer-health', icon: HeartPulse },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Billing', href: '/admin/billing', icon: CreditCard },
    { name: 'Feature Flags', href: '/admin/features', icon: Flag },
    { name: 'Learn Catalog', href: '/admin/learn', icon: GraduationCap },
    { name: 'Class Rosters', href: '/admin/classes', icon: ClipboardList },
    { name: 'Demo Requests', href: '/admin/demo-requests', icon: Mail },
    { name: 'Inquiries', href: '/admin/upgrade-requests', icon: Inbox },
    { name: 'Support', href: '/admin/support-requests', icon: LifeBuoy },
    { name: 'Broadcasts', href: '/admin/broadcasts', icon: Send },
    { name: 'Templates', href: '/admin/templates', icon: Palette },
    { name: 'Activity', href: '/admin/activity', icon: Activity },
    { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
    { name: 'Website Analytics', href: '/admin/web-analytics', icon: Globe },
    { name: 'Front Door', href: '/admin/front-door', icon: DoorOpen },
    { name: 'Deliveries', href: '/admin/deliveries', icon: PackageCheck },
    { name: 'API Calls', href: '/admin/api-calls', icon: ScrollText },
    { name: 'Organizations', href: '/admin/organizations', icon: Building2 },
  ];

  return (
    <div className="flex h-screen flex-col bg-background overflow-hidden md:block">
      {/* Phone: the sidebar has no room, so the links scroll in a strip */}
      <nav aria-label="Admin" className="flex shrink-0 gap-1 overflow-x-auto border-b bg-card p-2 md:hidden">
        {navigation.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            className="whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            activeProps={{ className: 'bg-accent text-accent-foreground' }}
          >
            {item.name}
          </Link>
        ))}
      </nav>

      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-64 border-r bg-card md:block">
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center border-b px-6">
            <h1 className="text-xl font-bold">Admin Console</h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
                activeProps={{
                  className: "bg-accent text-accent-foreground"
                }}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>

          {/* User section */}
          <div className="border-t p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium truncate max-w-[120px]">
                    {user?.email?.split('@')[0]}
                  </span>
                  <span className="text-xs text-muted-foreground">Admin</span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSignOut}
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="min-h-0 flex-1 overflow-y-auto md:h-full md:pl-64">
        <div className="container mx-auto p-4 pb-12 md:p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
